import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingCart, Wrench, Phone, Mail, Package } from "lucide-react";
import { MaskLine, Reveal } from "./Reveal";
import { ContactForm } from "./ContactForm";
import { DelerForm } from "./DelerForm";
import { CONTACT } from "../../lib/data";

const TABS = [
  { id: "salg", label: "Salg", icon: ShoppingCart, email: CONTACT.sales, desc: "Forespørsel om utstyr, tilbud og prosjektering." },
  { id: "service", label: "Service", icon: Wrench, email: CONTACT.service, desc: "Meld inn feil og servicebehov." },
  { id: "deler", label: "Bestill deler", icon: Package, email: CONTACT.service, desc: "Bestill reservedeler — last gjerne opp bilde av merkeskiltet." },
];

export const Kontakt = () => {
  const [active, setActive] = useState("salg");
  const current = TABS.find((t) => t.id === active);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail && TABS.some((t) => t.id === e.detail)) setActive(e.detail);
    };
    window.addEventListener("kontakt:set-tab", handler);
    return () => window.removeEventListener("kontakt:set-tab", handler);
  }, []);

  return (
    <section id="kontakt" className="bg-bone py-24 sm:py-32" data-testid="kontakt-section">
      <div className="max-w-[1600px] mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-12 gap-10 lg:gap-6">
          {/* Left info */}
          <div className="col-span-12 lg:col-span-4">
            <Reveal>
              <span className="font-oswald uppercase text-xs tracking-[0.3em] text-redx">Ta kontakt</span>
            </Reveal>
            <h2 className="font-oswald font-bold uppercase tracking-tight leading-[0.9] text-5xl sm:text-7xl mt-4 text-ink">
              <MaskLine>La oss</MaskLine>
              <MaskLine delay={0.1}>snakke</MaskLine>
            </h2>
            <div className="mt-10 space-y-5">
              <a href={`tel:+47${CONTACT.phoneRaw}`} data-testid="kontakt-phone" className="flex items-center gap-4 group">
                <span className="bg-ink text-white p-3 group-hover:bg-redx transition-colors"><Phone size={18} /></span>
                <span className="font-oswald uppercase text-2xl tracking-tight text-ink group-hover:text-redx transition-colors">{CONTACT.phone}</span>
              </a>
              <a href={`mailto:${CONTACT.sales}`} data-testid="kontakt-email-sales" className="flex items-center gap-4 group">
                <span className="bg-ink text-white p-3 group-hover:bg-redx transition-colors"><Mail size={18} /></span>
                <span className="text-ink/70 group-hover:text-redx transition-colors break-all">{CONTACT.sales}</span>
              </a>
              <a href={`mailto:${CONTACT.service}`} data-testid="kontakt-email-service" className="flex items-center gap-4 group">
                <span className="bg-ink text-white p-3 group-hover:bg-redx transition-colors"><Wrench size={18} /></span>
                <span className="text-ink/70 group-hover:text-redx transition-colors break-all">{CONTACT.service}</span>
              </a>
            </div>
          </div>

          {/* Right form card */}
          <div className="col-span-12 lg:col-span-8">
            <div className="bg-paper border border-ink/15 p-6 sm:p-12">
              {/* Tabs */}
              <div className="flex flex-wrap gap-0 border-b border-ink/15 mb-10">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setActive(t.id)}
                    data-testid={`tab-${t.id}`}
                    className={`relative flex items-center gap-2 px-6 py-4 font-oswald uppercase tracking-widest text-sm transition-colors duration-200 ${
                      active === t.id ? "text-ink" : "text-ink/40 hover:text-ink/70"
                    }`}
                  >
                    <t.icon size={16} /> {t.label}
                    {active === t.id && (
                      <motion.span layoutId="tab-underline" className="absolute left-0 right-0 -bottom-px h-[3px] bg-redx" />
                    )}
                  </button>
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.3 }}
                >
                  <p className="text-ink/60 mb-8">{current.desc} Sendes til <span className="text-ink font-medium">{current.email}</span></p>
                  {active === "deler" ? <DelerForm /> : <ContactForm kind={active} />}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
